import flow from 'lodash/flow';
import pick from 'lodash/pick';
import { decamelizeKeys, camelize, decamelize } from 'humps';
import { warn } from 'utils/console';

import walkNestedData from 'common/utils/forms/walkNestedData';

import { INPUT_TYPE_CONFIGURATIONS } from '../constants/bootstrapKeys';

/**
 * @param {*} value
 */
function isTypeofNumber(value) {
    return typeof value === 'number';
}

/**
 * casts all IDs that are numbers into strings. Helps keep front-end code IDs of the same type.
 * @param {Object} formNode
 * @param {Object}
 */
function castIdsToStrings(formNode) {
    const result = { ...formNode };

    if (isTypeofNumber(result.id)) {
        result.id = `${result.id}`;
    }

    if (result.logic) {
        result.logic = result.logic.map((originalLogic) => {
            const logic = { ...originalLogic };

            if (isTypeofNumber(logic.target)) {
                logic.target = `${logic.target}`;
            }

            if (
                isTypeofNumber(logic.operand_left) &&
                // @TODO, use another PR I have on the works to read from bootstrap.
                (logic.operand_left_type === 'question' ||
                    logic.operand_left_type === 'group' ||
                    logic.operand_left_type === 'form')
            ) {
                logic.operand_left = `${logic.operand_left}`;
            }

            if (
                isTypeofNumber(logic.operand_right) &&
                // @TODO, use another PR I have on the works to read from bootstrap.
                (logic.operand_right_type === 'question' ||
                    logic.operand_right_type === 'group' ||
                    logic.operand_right_type === 'form')
            ) {
                logic.operand_right = `${logic.operand_right}`;
            }

            return logic;
        });
    }

    return result;
}

/**
 * @param {Object} formNode
 * @return {Object}
 */
function camelizeInputType(formNode) {
    let updatedNode;
    if (formNode.input_type) {
        updatedNode = {
            ...formNode,
            input_type: camelize(formNode.input_type),
        };
    }
    return updatedNode || formNode;
}

/**
 * Back-end likes input type to be snake case. so "splashMultiselect" -> 'splash_multiselect
 *
 * @param {Object} formNode
 * @return {Object}
 */
function decamelizeInputType(formNode) {
    let updatedNode;
    if (formNode.inputType) {
        updatedNode = {
            ...formNode,
            inputType: decamelize(formNode.inputType),
        };
    }
    return updatedNode || formNode;
}

/**
 * Removes the node.config keys that don't apply to the given node. Back-end would reject a save
 * if front-end passed invalid keys in there.
 *
 * @param {Object} formNode
 * @param {Object} inputTypeConfigurations - comes from bootstrap data.
 * @return {Object}
 */
function removeInvalidConfigKeys(formNode, inputTypeConfigurations) {
    let updatedNode = formNode;

    if (updatedNode.config) {
        updatedNode = { ...formNode };
        updatedNode.config = pick(
            updatedNode.config,
            Object.keys(inputTypeConfigurations[updatedNode.inputType] || {}),
        );
    }
    return updatedNode;
}

/**
 * Transforms API response form descriptor into the data structure front-end handles.
 *
 * @param  {Object} payloadFormData - form descriptor, with keys id and root among others.
 * @return {Object}
 */
export function transformInputPayloadFormData(payloadFormData) {
    const formData = { ...payloadFormData };

    if (isTypeofNumber(formData.id)) {
        formData.id = `${formData.id}`;
    }

    if (formData.root) {
        formData.root = walkNestedData(formData.root || {}, (formNode) =>
            flow(
                (transformedNode) => castIdsToStrings(transformedNode),
                (transformedNode) => camelizeInputType(transformedNode),
            )(formNode),
        );
    } else {
        warn('FormBuilder', 'ID transformation - no formData.root, function might be receiving wrong structure.');
    }

    return formData;
}

/**
 * Transforms front-end data format into the form descriptor API accepts.
 *
 * @param  {Object} payloadFormData - form descriptor, with keys id and root among others.
 * @param  {Object} params.inputTypeConfigurations - comes from bootstrap data.
 * @return {Object}
 */
export function transformOutputPayloadFormData(payloadFormData, bootstrap) {
    const formData = { ...payloadFormData };
    const inputTypeConfigurations = bootstrap[INPUT_TYPE_CONFIGURATIONS];

    if (!formData.root) {
        warn('FormBuilder', 'ID transformation - no formData.root, function might be receiving wrong structure.');
        return formData;
    }

    formData.root = walkNestedData(formData.root || {}, (formNode) =>
        flow(
            (transformedNode) => removeInvalidConfigKeys(transformedNode, inputTypeConfigurations),
            (transformedNode) => decamelizeInputType(transformedNode),
        )(formNode),
    );

    const decamelizedFormData = decamelizeKeys(formData);

    return decamelizedFormData;
}

/**
 * Transforms a question descriptor to be sent to the API to be saved.
 *
 * @param  {Object} question
 * @return {Object}
 */
export const transformOutputQuestionData = flow(
    (question) => decamelizeInputType(question),
    (transformedQuestion) => decamelizeKeys(transformedQuestion),
);
